import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  #slideObserver;

  /**
   * An auto-activated model (data-auto-activate) loads itself as soon as its slide is shown
   * and then keeps playing. It is never paused through Shopify's ModelViewerUI: that pause
   * resets the camera and leaves model-viewer's camera animation disabled afterwards, and
   * model-viewer already stops rendering by itself while it is off screen.
   */
  get #autoActivate() {
    return this.hasAttribute('data-auto-activate');
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#autoActivate) return;

    const slide = this.closest('slideshow-slide');
    if (!slide) {
      this.loadContent(false);
      return;
    }

    const activateSelectedModel = () => {
      if (slide.getAttribute('aria-hidden') === 'false' && !this.hasAttribute('data-media-loaded')) {
        this.loadContent(false);
      }
    };

    this.#slideObserver = new MutationObserver(activateSelectedModel);
    this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    activateSelectedModel();
  }

  loadContent(focus = true) {
    if (this.#autoActivate && this.hasAttribute('data-media-loaded')) return;
    super.loadContent(focus);

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#slideObserver?.disconnect();
  }

  pauseMedia() {
    if (this.#autoActivate && this.modelViewerUI) return;
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
    this.#restoreCamera();
  }

  /**
   * Re-applies the camera attributes as properties once the model has loaded and makes sure
   * the poster counts as dismissed. Shopify's UI only makes the poster transparent, and
   * model-viewer 1.12 skips its camera animation (no rotation) while it thinks the poster is
   * still shown; the camera limits also only stick once they are set after loading.
   */
  #restoreCamera() {
    const element = this.querySelector('model-viewer');
    if (!element) return;
    let interacted = false;
    const attr = (name) => element.getAttribute(name);
    const applyLimits = () => {
      // Shopify's UI only makes the poster transparent; model-viewer still treats it as shown
      // and then skips its camera animation, which leaves the model impossible to rotate.
      if (element.loaded && typeof element.dismissPoster === 'function') element.dismissPoster();
      if (attr('min-camera-orbit')) element.minCameraOrbit = attr('min-camera-orbit');
      if (attr('max-camera-orbit')) element.maxCameraOrbit = attr('max-camera-orbit');
      if (attr('min-field-of-view')) element.minFieldOfView = attr('min-field-of-view');
      if (attr('max-field-of-view')) element.maxFieldOfView = attr('max-field-of-view');
    };
    // Intro: the model loads straight on (camera-orbit, no perspective) and then glides to the
    // final view (data-ap-final-orbit) so the visitor sees that this is a 3D model. Only while
    // the visitor has not rotated it yet.
    const introOrbit = attr('camera-orbit');
    const finalOrbit = attr('data-ap-final-orbit') || introOrbit;
    const parseOrbit = (value) => {
      const m = /^(-?[\d.]+)deg\s+(-?[\d.]+)deg\s+(\S+)$/.exec(value || '');
      return m ? { theta: +m[1], phi: +m[2], radius: m[3] } : null;
    };
    // The glide is driven frame by frame, not by model-viewer's own damping: model-viewer
    // queues a camera jump whenever a limit changes or its post-load framing runs, and such a
    // jump would otherwise cut the glide short. With per-frame goals a jump lands on the
    // current frame's position at most. It only starts once model-viewer reports the poster
    // as dismissed: until then it does not move its camera at all.
    let glideStarted = false;
    const startGlide = () => {
      if (glideStarted || interacted) return;
      glideStarted = true;
      const from = parseOrbit(introOrbit);
      const to = parseOrbit(finalOrbit);
      if (!from || !to) return;
      const duration = 1200;
      setTimeout(() => {
        if (interacted) return;
        const start = performance.now();
        const step = () => {
          if (interacted) return;
          const p = Math.min(1, (performance.now() - start) / duration);
          const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
          const theta = from.theta + (to.theta - from.theta) * e;
          const phi = from.phi + (to.phi - from.phi) * e;
          element.cameraOrbit = `${theta.toFixed(3)}deg ${phi.toFixed(3)}deg ${to.radius}`;
          if (p < 1) requestAnimationFrame(step);
          else setTimeout(applyLimits, 500);
        };
        requestAnimationFrame(step);
      }, 150);
    };
    // model-viewer 1.12 only animates its camera once it regards the poster as dismissed
    // ("hasTransitioned"). dismissPoster() is ignored while the scene is not ready yet, so it
    // is repeated until that state is reached; the glide starts at that moment.
    const transitioned = () => {
      try {
        let proto = element;
        while (proto) {
          const sym = Object.getOwnPropertySymbols(proto).find((s) => s.description === 'hasTransitioned');
          if (sym) return element[sym]() === true;
          proto = Object.getPrototypeOf(proto);
        }
      } catch (e) {
        // fall through to the poster check
      }
      const poster = element.shadowRoot && element.shadowRoot.querySelector('.slot.poster');
      return poster ? !poster.classList.contains('show') : true;
    };
    const playIntro = () => {
      applyLimits();
      if (attr('field-of-view')) element.fieldOfView = attr('field-of-view');
      let attempts = 0;
      const retry = setInterval(() => {
        attempts += 1;
        if (transitioned()) {
          clearInterval(retry);
          startGlide();
          return;
        }
        if (attempts > 80) {
          clearInterval(retry);
          return;
        }
        if (typeof element.dismissPoster === 'function') element.dismissPoster();
      }, 100);
    };
    if (element.loaded) playIntro();
    else element.addEventListener('load', playIntro, { once: true });
    // model-viewer can show the poster again later (its own visibility observer re-runs the
    // source update), so the poster is dismissed once more on the first pointer interaction.
    element.addEventListener(
      'pointerdown',
      () => {
        interacted = true;
        if (element.loaded && typeof element.dismissPoster === 'function') element.dismissPoster();
      },
      { capture: true, signal: this.#abortController.signal }
    );
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    if (this.#autoActivate) return;

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
